import PageState from "shared/util/PageState";
import Api from "apps/discovery/util/web";
import {
	SECTIONS,
	DISCOVERY_PAGE_STATE_KEY,
} from "apps/discovery/util/consts";
import { QUESTIONS_PAGE_STATE_KEY } from "apps/questions/util/constants";


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	isMounted: false,
	sections: {},
	sectionOrder: [],
	promos: {
		showInstagramPromo: false,
		showPhotoUploadPromo: false,
	},
	questionMatchResults: {},
};

const getInitialState = () => {
	let initialState;
	let lastPageState;

	if (
		process.env.NODE_ENV !== "test" &&
		PageState &&
		typeof PageState.get === "function"
	) {
		lastPageState = PageState.get(DISCOVERY_PAGE_STATE_KEY);
	}

	if (lastPageState) {
		initialState = lastPageState;
	} else if (typeof window !== "undefined" && window.__PRELOADED_STATE__) {
		initialState = window.__PRELOADED_STATE__;
	} else {
		initialState = INITIAL_STATE;
	}

	return initialState;
};


// action types
// ----------------------------------------------------------------------------

export const ON_MOUNT = "DISCOVERY.ON_MOUNT";

export const GET_SECTION = "DISCOVERY.GET_SECTION";
export const GET_SECTION_SUCCESS = "DISCOVERY.GET_SECTION_SUCCESS";
export const GET_SECTION_ERROR = "DISCOVERY.GET_SECTION_ERROR";

export const SEARCH_INTEREST = "DISCOVERY.SEARCH_INTEREST";
export const SEARCH_INTEREST_SUCCESS = "DISCOVERY.SEARCH_INTEREST_SUCCESS";
export const SEARCH_INTEREST_ERROR = "DISCOVERY.SEARCH_INTEREST_ERROR";

export const GET_INTEREST_MATCHES = "DISCOVERY.GET_INTEREST_MATCHES";
export const GET_INTEREST_MATCHES_SUCCESS = "DISCOVERY.GET_INTEREST_MATCHES_SUCCESS";
export const GET_INTEREST_MATCHES_ERROR = "DISCOVERY.GET_INTEREST_MATCHES_ERROR";

export const GET_QUESTION_MATCHES = "DISCOVERY.GET_QUESTION_MATCHES";
export const GET_QUESTION_MATCHES_SUCCESS = "DISCOVERY.GET_QUESTION_MATCHES_SUCCESS";
export const GET_QUESTION_MATCHES_ERROR = "DISCOVERY.GET_QUESTION_MATCHES_ERROR";

export const CHANGE_PAGE = "DISCOVERY.CHANGE_PAGE";
export const CHANGE_IMPORTANT_QUESTION = "DISCOVERY.CHANGE_IMPORTANT_QUESTION";


// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case ON_MOUNT: {
			if (
				process.env.NODE_ENV !== "test" &&
				PageState &&
				typeof PageState.set === "function"
			) {
				PageState.set({ [DISCOVERY_PAGE_STATE_KEY]: null });
			}

			return {
				...state,
				isMounted: true,
			};
		}

		case GET_SECTION: {
			return state;
		}

		case GET_SECTION_SUCCESS: {
			const newSection = action.payload.section;
			const newState = {
				...state,
				sections: {
					...state.sections,
					[action.payload.section.section_id]: newSection,
				},
			};

			// if we are fetching an important question section, add its results to
			// the important question section cache
			if (
				newSection.section_id === SECTIONS.important_questions &&
				newSection.results.rows && newSection.results.rows.length
			) {
				newState.questionMatchResults = {
					...newState.questionMatchResults,
					[newSection.results.rows[0].id]: newSection.results.rows[0],
				};
			}

			return newState;
		}

		case GET_SECTION_ERROR: {
			const sectionId = action.payload.section.section_id;

			return {
				...state,
				sections: {
					...state.sections,
					[sectionId]: {
						...state.sections[sectionId],
						error: true,
					},
				},
			};
		}

		case SEARCH_INTEREST: {
			return {
				...state,
				isSearchingInterest: true,
				interestResults: [],
			};
		}

		case SEARCH_INTEREST_SUCCESS: {
			let results = [];

			if (action.payload.interests && action.payload.interests.data) {
				results = action.payload.interests.data;

				results = results.map((result) => {
					return {
						text: result.name,
						subtext: null,
						id: result.id,
					};
				});
			}

			return {
				...state,
				isSearchingInterest: false,
				interestResults: results,
			};
		}

		case SEARCH_INTEREST_ERROR: {
			return {
				...state,
				isSearchingInterest: false,
				interestResults: [],
			};
		}

		case GET_INTEREST_MATCHES: {
			return {
				...state,
				isGettingInterestMatches: true,
			};
		}

		case GET_INTEREST_MATCHES_SUCCESS: {
			// the payload of this action is row data, along with `id`, which is the
			// id of the interest that was searched. here, we decide what to do with
			// the returned data; if there aren't any user interest searches, we can
			// add this, but if there are more than the max we'll kill the last one.
			if (!state.sections[SECTIONS.interests]) {
				return { ...state };
			}

			const rows = [action.payload.row];

			return {
				...state,
				isGettingInterestMatches: false,
				sections: {
					...state.sections,
					[SECTIONS.interests]: {
						...state.sections[SECTIONS.interests],
						results: {
							...state.sections[SECTIONS.interests].results,
							rows,
						},
					},
				},
			};
		}

		case GET_INTEREST_MATCHES_ERROR: {
			return {
				...state,
				isGettingInterestMatches: false,
			};
		}

		case GET_QUESTION_MATCHES: {
			return {
				...state,
				isGettingQuestionMatches: true,
			};
		}

		case GET_QUESTION_MATCHES_SUCCESS: {
			if (!state.sections[SECTIONS.important_questions]) {
				return { ...state };
			}

			return {
				...state,
				isGettingQuestionMatches: false,
				sections: {
					...state.sections,
					[SECTIONS.important_questions]: {
						...state.sections[SECTIONS.important_questions],
						results: {
							...state.sections[SECTIONS.important_questions].results,
							rows: [action.payload.row],
						},
					},
				},
				questionMatchResults: {
					...state.questionMatchResults,
					[action.payload.questionId]: action.payload.row,
				},
			};
		}

		case GET_QUESTION_MATCHES_ERROR: {
			return {
				...state,
				isGettingQuestionMatches: false,
			};
		}

		case CHANGE_PAGE: {
			const { sectionId, page, rowId } = action.payload;

			const results = {
				...state.sections[sectionId].results,
			};

			if (results.rows.length === 1) {
				results.rows[0].currentPage = page;
			} else {
				results.rows.forEach((row, i) => {
					if (row.id === rowId) {
						results.rows[i] = {
							...row,
							currentPage: page,
						};
					}
				});
			}

			const newState = {
				...state,
				sections: {
					...state.sections,
					[sectionId]: {
						...state.sections[sectionId],
						results: {
							...state.sections[sectionId].results,
							...results,
						},
					},
				},
			};

			return newState;
		}

		case CHANGE_IMPORTANT_QUESTION: {
			return {
				...state,
				sections: {
					...state.sections,
					[SECTIONS.important_questions]: {
						...state.sections[SECTIONS.important_questions],
						header_component: {
							...state.sections[SECTIONS.important_questions].header_component,
							currentPage: action.payload.page,
						},
					},
				},
			};
		}

		default: {
			return state;
		}
	}
}


// actions
// ----------------------------------------------------------------------------

export const mount = () => {
	return { type: ON_MOUNT };
};

export const getSection = (params) => {
	return (dispatch) => {
		dispatch({ type: GET_SECTION });

		Api.getSection(params)
			.then((section) => {
				dispatch({
					type: GET_SECTION_SUCCESS,
					payload: {
						section,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_SECTION_ERROR,
					payload: {
						section: params,
						error,
					},
				});
			});
	};
};

export const searchInterest = (query) => {
	return (dispatch) => {
		dispatch({ type: SEARCH_INTEREST });

		Api.searchInterest(query)
			.then((interests) => {
				dispatch({
					type: SEARCH_INTEREST_SUCCESS,
					payload: {
						query,
						interests,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: SEARCH_INTEREST_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const getInterestMatches = (params) => {
	return (dispatch) => {
		dispatch({ type: GET_INTEREST_MATCHES });

		Api.getInterestMatches(params)
			.then((res) => {
				dispatch({
					type: GET_INTEREST_MATCHES_SUCCESS,
					payload: {
						row: res,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_INTEREST_MATCHES_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const getQuestionMatches = (questionId) => {
	return (dispatch, getStore) => {
		dispatch({ type: GET_QUESTION_MATCHES });

		const store = getStore().discovery;

		// if this question exists in the store, return the cached version instead
		// of hitting the api
		if (store.questionMatchResults && store.questionMatchResults[questionId]) {
			dispatch({
				type: GET_QUESTION_MATCHES_SUCCESS,
				payload: {
					row: store.questionMatchResults[questionId],
					questionId,
				},
			});

			return;
		}

		Api.getQuestionMatches(questionId)
			.then((res) => {
				dispatch({
					type: GET_QUESTION_MATCHES_SUCCESS,
					payload: {
						row: res,
						questionId,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_QUESTION_MATCHES_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const changePage = (payload) => {
	return {
		type: CHANGE_PAGE,
		payload,
	};
};

export const changeImportantQuestion = (page) => {
	return {
		type: CHANGE_IMPORTANT_QUESTION,
		payload: {
			page,
		},
	};
};

export const visitUser = (url) => {
	return (dispatch, getStore) => {
		const store = getStore();

		PageState.set({ [DISCOVERY_PAGE_STATE_KEY]: store.discovery });
		PageState.set({ [QUESTIONS_PAGE_STATE_KEY]: store.questions });

		window.location.href = `${window.location.origin}${url}`;
	};
};



// WEBPACK FOOTER //
// ./src/ducks/discovery.js